import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { ChoiceChip } from '@components';
import {
  CandidateRegion,
  IStatusFacet,
  isSenioritySelected,
  toggle,
  toggleSeniority,
} from '@utils';
import { SeniorityOrder } from '@constants';

export interface ICandidateFilterSectionsProps {
  regions: CandidateRegion[];
  seniorities: string[];
  statusFacets: IStatusFacet[];
  selectedRegions: CandidateRegion[];
  selectedSeniorities: string[];
  selectedStatuses: string[];
  onChange: (patch: {
    regions?: CandidateRegion[];
    seniorities?: string[];
    statuses?: string[];
  }) => void;
}

export const offersChoice = (optionCount: number) => optionCount > 1;

export const FilterSectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="caption"
    component="h3"
    color="text.secondary"
    sx={{
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0.32px',
      textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

const FilterSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) =>
  offersChoice(React.Children.count(children)) ? (
    <Stack gap={1}>
      <FilterSectionTitle>{title}</FilterSectionTitle>
      <Box display="flex" flexWrap="wrap" gap={0.75}>
        {children}
      </Box>
    </Stack>
  ) : null;

export const CandidateFilterSections: React.FC<
  ICandidateFilterSectionsProps
> = ({
  regions,
  seniorities,
  statusFacets,
  selectedRegions,
  selectedSeniorities,
  selectedStatuses,
  onChange,
}) => {
  const orderedSeniorities = [...seniorities].sort(
    (a, b) => SeniorityOrder.indexOf(a) - SeniorityOrder.indexOf(b),
  );

  return (
    <>
      <FilterSection title="By stage">
        {statusFacets.map((facet) => (
          <ChoiceChip
            key={facet.key}
            selected={selectedStatuses.includes(facet.key)}
            onClick={() =>
              onChange({ statuses: toggle(selectedStatuses, facet.key) })
            }
          >
            {facet.label}
          </ChoiceChip>
        ))}
      </FilterSection>

      <FilterSection title="Region">
        {regions.map((region) => (
          <ChoiceChip
            key={region}
            selected={selectedRegions.includes(region)}
            onClick={() =>
              onChange({ regions: toggle(selectedRegions, region) })
            }
          >
            {region}
          </ChoiceChip>
        ))}
      </FilterSection>

      <FilterSection title="Seniority">
        {orderedSeniorities.map((seniority) => (
          <ChoiceChip
            key={seniority}
            selected={isSenioritySelected(selectedSeniorities, seniority)}
            onClick={() =>
              onChange({
                seniorities: toggleSeniority(
                  selectedSeniorities,
                  seniority,
                  seniorities,
                ),
              })
            }
          >
            {seniority}
          </ChoiceChip>
        ))}
      </FilterSection>
    </>
  );
};
